import type {
  MetadataFieldId,
  SignalMetadataSchema,
} from "@repo/app-types/entities/signal";
import type { TestSignalProposalToolOutput } from "@repo/app-types/entities/signalConfigurationTools";
import { UNKNOWN_CONTACT_DISPLAY_NAME } from "@repo/app-types/helpers/utilities";
import { Size } from "@repo/ui/helpers/helpers";
import { Modal } from "@repo/ui/Modal/modal";
import { ModalHeader } from "@repo/ui/Modal/modalHeader";
import { IconUser } from "@tabler/icons-react";
import { DateTime } from "luxon";
import { type FC, useMemo } from "react";
import {
  formatMetadataFieldKey,
  formatMetadataValue,
} from "../../signals/signalStatsFormatters.js";
import type { QuoteMatchMap } from "../../teammatePerformance/gradeValidationHelpers.js";
import { ParagraphsContainer } from "../../teammatePerformance/paragraphsContainer.js";

export interface SignalTestMatchesModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: TestSignalProposalToolOutput;
  metadataSchema: SignalMetadataSchema;
}

type InteractionMatch =
  TestSignalProposalToolOutput["interactions_with_matches"][number];

type Match = InteractionMatch["matches"][number];

const EMPTY_QUOTE_MATCH_MAP: QuoteMatchMap = {
  quoteToMatchesMap: new Map(),
  recordingToMatchesMap: new Map(),
};

export const SignalTestMatchesModal: FC<SignalTestMatchesModalProps> = ({
  isOpen,
  onClose,
  data,
  metadataSchema,
}) => {
  const { interactions_with_matches, summary } = data;

  return (
    <Modal isOpen={isOpen} onCancel={onClose} size={Size.LARGE}>
      <ModalHeader onClose={onClose}>
        Matched conversations ({summary.matched_interactions})
      </ModalHeader>

      <div className="mt-6 flex max-h-[60vh] flex-col gap-3 overflow-y-auto">
        {interactions_with_matches.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            No matches.
          </p>
        ) : (
          interactions_with_matches.flatMap((interaction) =>
            interaction.matches.map((match) => (
              <MatchCard
                key={`${interaction.interaction_id}-${match.recording_id}`}
                interaction={interaction}
                match={match}
                metadataSchema={metadataSchema}
              />
            )),
          )
        )}
      </div>
    </Modal>
  );
};

const MatchCard: FC<{
  interaction: InteractionMatch;
  match: Match;
  metadataSchema: SignalMetadataSchema;
}> = ({ interaction, match, metadataSchema }) => {
  const formattedDate = match.recorded_at
    ? DateTime.fromISO(match.recorded_at).toFormat("LLL d · h:mma")
    : null;

  const metadataEntries = useMemo(() => {
    const fieldLabelById = new Map(
      metadataSchema.fields.map((f) => [f.id, f.key]),
    );
    if (!match.extracted_metadata) return [];
    return Object.entries(match.extracted_metadata)
      .map(([fieldId, value]) => {
        const label = fieldLabelById.get(fieldId as MetadataFieldId);
        if (!label) return null;
        return {
          label: formatMetadataFieldKey(label),
          value: formatMetadataValue(value),
        };
      })
      .filter((e): e is { label: string; value: string } => e !== null);
  }, [match.extracted_metadata, metadataSchema]);

  const hasSummarySection =
    Boolean(match.explanation) || metadataEntries.length > 0;

  return (
    <div className="rounded-lg border border-border-light bg-white">
      <div className="border-b border-gray-100 p-4">
        <div className="flex items-center gap-2 text-sm">
          <span className="font-semibold text-foreground">
            {interaction.contact_name ?? UNKNOWN_CONTACT_DISPLAY_NAME}
          </span>
          {formattedDate ? (
            <>
              <span className="text-gray-400">&middot;</span>
              <span className="text-xs text-muted-foreground">
                {formattedDate}
              </span>
            </>
          ) : null}
          <span className="text-gray-400">&middot;</span>
          <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <IconUser className="size-4 shrink-0" />
            <span className="font-medium text-foreground">
              {interaction.teammate_name}
            </span>
          </span>
        </div>
      </div>

      {hasSummarySection ? (
        <div className="space-y-2 p-4">
          <h3 className="mb-1 text-xs font-semibold text-muted-foreground">
            Summary
          </h3>
          {match.explanation ? (
            <p className="text-sm text-foreground">{match.explanation}</p>
          ) : null}
          {metadataEntries.length > 0 ? (
            <dl className="space-y-1 text-xs">
              {metadataEntries.map((entry) => (
                <div key={entry.label} className="flex gap-2">
                  <dt className="font-medium text-gray-400">{entry.label}</dt>
                  <dd className="text-muted-foreground">{entry.value}</dd>
                </div>
              ))}
            </dl>
          ) : null}
        </div>
      ) : null}

      {match.analysis.length > 0 ? (
        <div className="border-t border-gray-100 p-4">
          <h3 className="mb-2 text-xs font-semibold text-muted-foreground">
            Analysis
          </h3>
          <ParagraphsContainer
            recordings={[]}
            paragraphs={match.analysis}
            hasCompleted={true}
            quoteMatches={EMPTY_QUOTE_MATCH_MAP}
            onQuotePress={() => {}}
          />
        </div>
      ) : null}
    </div>
  );
};
